/* =========================================================================
   VD GRID ENGINE — shared incremental product-grid weaver (2026-08-05)
   Single source of truth for vd-collection + vd-discover. Replaces two
   hand-copied engines that drifted (root cause of the Load-more scroll-jump
   saga: a fix landed in one copy and not the other).

   Invariants — any change MUST preserve all four:
     1. The grid is NEVER torn down: layout() computes the desired sequence
        and diff-syncs it in place, touching only positions that changed.
     2. Per-row weave decisions (promo / no promo, and which node) are
        PERMANENT until reset() or a column-count change: appended data can
        never retro-insert content above the viewport.
     3. Consequence of 1+2: Load more is a pure append below the fold — the
        user's viewport cannot move.
     4. keepAnchored(): scroll-drift compensation with a one-viewport sanity
        clamp — a "correction" larger than the viewport is pathological and
        is dropped rather than flinging the user.

   API:
     vdGridEngine.createWeaver(opts) -> { layout(), reset() }
       opts.grid            element OR function returning the grid element
       opts.productSelector querySelectorAll selector for product cells
       opts.seed()          number  — weave randomisation seed
       opts.every()         number  — promo row interval (rows 3, 3+n, …)
       opts.makePromo(pc)   node|null — promo factory for slot index pc
                            (return null to decline: the cell gets a product,
                            and "no promo" is recorded permanently)
       opts.fullWidthRow(row, ctx) node|null — optional full-width insert
                            after `row` completed rows (breaker / UGC row);
                            called ONCE ever per row, result cached
       opts.onSync()        optional — after DOM sync (e.g. init swatches)
       opts.onReset()       optional — when caches wipe (reset()/col change)
     vdGridEngine.keepAnchored(el, fn)
     vdGridEngine.colCount(grid) / vdGridEngine.srnd(seed, n)
     vdGridEngine.promoPool.{get,pick,card} — shared Theme-Settings Promo pool
                            helpers (added 2026-08-13, pool goes site-wide)
   ========================================================================= */
(function () {
  if (window.vdGridEngine) return;

  function srnd(seed, n) { var x = Math.sin((seed + 1) * 9301 + n * 49297) * 233280; return x - Math.floor(x); }

  function colCount(grid) {
    var t = getComputedStyle(grid).gridTemplateColumns || '';
    var n = t.split(' ').filter(Boolean).length;
    return n || 1;
  }

  function keepAnchored(el, fn) {
    if (!el) { fn(); return; }
    var b = el.getBoundingClientRect().top;
    fn();
    if (el.offsetParent !== null) {
      var d = el.getBoundingClientRect().top - b;
      if (d && Math.abs(d) >= 1 && Math.abs(d) < window.innerHeight) window.scrollBy(0, d);
    }
  }

  function createWeaver(opts) {
    var cache = {}, cacheCols = 0;
    function wipe() { cache = {}; cacheCols = 0; if (opts.onReset) opts.onReset(); }
    function gridEl() { return typeof opts.grid === 'function' ? opts.grid() : opts.grid; }

    function layout() {
      var grid = gridEl(); if (!grid) return;
      var products = Array.prototype.slice.call(grid.querySelectorAll(opts.productSelector));
      if (!products.length) return;
      var cols = colCount(grid); if (cols < 1) cols = 1;
      if (cols !== cacheCols) { wipe(); cacheCols = cols; }   // breakpoint/column change → legitimate re-weave
      var rows = cache.rows = cache.rows || {};
      var fw = cache.fw = cache.fw || {};
      var seed = opts.seed(), every = opts.every() || 4;

      var seq = [], pi = 0, row = 0, pc = 0;
      while (pi < products.length) {
        var dec = rows[row], promoCol = -1, promoNode = null;
        if (dec === undefined) {
          if (row >= 3 && (row - 3) % every === 0 && (products.length - pi) >= cols) {
            promoCol = Math.floor(srnd(seed, row) * cols);
          }
        } else if (dec) { promoCol = dec.col; promoNode = dec.node; }
        for (var c = 0; c < cols; c++) {
          if (c === promoCol) {
            if (!promoNode) promoNode = opts.makePromo(pc) || null;
            if (promoNode) { seq.push(promoNode); pc++; }
            else if (pi < products.length) { seq.push(products[pi++]); }  // factory declined → product fills the cell
          } else if (pi < products.length) { seq.push(products[pi++]); }
        }
        if (dec === undefined) rows[row] = promoNode ? { col: promoCol, node: promoNode } : false;
        row++;
        if (opts.fullWidthRow) {
          var f = fw[row];
          if (f === undefined) { f = opts.fullWidthRow(row, { pi: pi, total: products.length }) || false; fw[row] = f; }
          if (f) seq.push(f);
        }
      }

      // incremental sync — touch ONLY mismatched positions (pure append on Load more)
      var k = 0;
      for (var i = 0; i < seq.length; i++) {
        var want = seq[i], cur = grid.children[k];
        if (cur !== want) grid.insertBefore(want, cur || null);
        k++;
      }
      while (grid.children.length > seq.length) grid.removeChild(grid.lastChild);
      if (opts.onSync) opts.onSync();
    }

    return { layout: layout, reset: wipe };
  }

  /* ---- Shared Theme-Settings Promo pool (2026-08-13, Hailin: pool goes site-wide) ----
     snippets/vd-promo-pool.liquid emits the pool as `.vd-promo-pool-data` JSON; these helpers
     give every surface the SAME weighted (priority) + seeded + no-repeat pick and card DOM.
       promoPool.get(scope)                  -> array (items tagged __i); scope = section root
       promoPool.pick(pool, seed, idx, used) -> item (records into `used`; cycles when exhausted)
       promoPool.card(item, itemClass)       -> <a> node; pass the surface's cell class
                                                (PLP 'vd-coll__item' / Discover 'vd-disc__item')
     NOTE: the PLP (vd-collection) still runs its identical INLINE copy of this logic — do NOT
     switch it to these helpers while the Intelligems PLP experiment is live; its code path must
     stay byte-untouched. Converge after the experiment ends. */
  function poolGet(scope) {
    var el = (scope || document).querySelector('.vd-promo-pool-data'); if (!el) return [];
    try { var a = JSON.parse(el.textContent.trim() || '[]'); a.forEach(function (p, i) { p.__i = i; }); return a; }
    catch (e) { return []; }
  }
  function poolPick(pool, seed, idx, used) {
    var avail = pool.filter(function (p) { return used.indexOf(p.__i) < 0; });
    if (!avail.length) { used.length = 0; avail = pool.slice(); }          // all shown once → reset, cycle again
    var tot = 0; avail.forEach(function (p) { tot += (p.w || 1); });
    var r = srnd(seed, idx * 7 + 3) * tot, acc = 0, chosen = avail[avail.length - 1];
    for (var k = 0; k < avail.length; k++) { acc += (avail[k].w || 1); if (r < acc) { chosen = avail[k]; break; } }
    used.push(chosen.__i); return chosen;
  }
  function poolEsc(s) { return s == null ? '' : String(s); }
  function poolCard(card, itemClass) {
    var a = document.createElement('a');
    a.className = (itemClass ? itemClass + ' ' : '') + 'vd-coll__promo vd-coll__promo--pool';
    a.href = card.link || '#'; a.style.backgroundImage = "url('" + card.img + "')";
    a.innerHTML = '<span class="vd-coll__promo-eyebrow vd-t-eyebrow">' + poolEsc(card.eyebrow) + '</span><span class="vd-coll__promo-title vd-t-display-3">' + poolEsc(card.title) + '</span>';
    return a;
  }

  window.vdGridEngine = { createWeaver: createWeaver, keepAnchored: keepAnchored, colCount: colCount, srnd: srnd, promoPool: { get: poolGet, pick: poolPick, card: poolCard } };
})();
